import React from 'react';
import { AutoFeatures } from '../types/GameTypes';
import webSocketService from '../services/WebSocketService';

interface FeaturePreviewProps {
  currentLevel: number;
  autoFeatures: AutoFeatures;
}

interface FeatureMilestone {
  level: number;
  name: string;
  description: string;
  featureKey?: keyof AutoFeatures; // Link to the specific auto-feature state key
}

// Define the milestones
const milestones: FeatureMilestone[] = [
  {
    level: 100,
    name: 'Rocket Launcher',
    description: 'Unlocks a special surprise!',
    // featureKey: undefined // No toggle for this one yet
  },
  {
    level: 50,
    name: 'Auto Feed',
    description: 'Automatically feeds the dragon when hunger is low.',
    featureKey: 'autoFeedEnabled',
  },
  {
    level: 25,
    name: 'Auto Play',
    description: 'Automatically plays with the dragon when fun is low.',
    featureKey: 'autoPlayEnabled',
  },
  {
    level: 10,
    name: 'Auto Clean',
    description: 'Automatically cleans poop when there\'s too much.',
    featureKey: 'autoCleanEnabled',
  },
];

const FeaturePreview: React.FC<FeaturePreviewProps> = ({ currentLevel, autoFeatures }) => {

  const handleToggle = (featureKey: keyof AutoFeatures, isEnabled: boolean) => {
    console.log(`Toggling ${featureKey} to ${!isEnabled}`);
    // Map the keyof AutoFeatures to the string expected by the backend
    let featureString = '';
    switch (featureKey) {
      case 'autoCleanEnabled': featureString = 'autoClean'; break;
      case 'autoPlayEnabled': featureString = 'autoPlay'; break;
      case 'autoFeedEnabled': featureString = 'autoFeed'; break;
    }
    if (featureString) {
      webSocketService.toggleAutoFeature(featureString, !isEnabled);
    }
  };

  // Calculate progress bar height (percentage of max level 100)
  const maxMilestoneLevel = 100;
  const progressPercent = Math.min(100, (currentLevel / maxMilestoneLevel) * 100);

  return (
    <div className="p-4 bg-gray-100 rounded-lg shadow-inner relative text-sm">
      <h3 className="text-lg font-semibold mb-3 text-center">Dragon Level: {currentLevel}</h3>

      <div className="flex items-end h-64"> {/* Container for bar and milestones */} 
        {/* Vertical Progress Bar */} 
        <div className="w-4 bg-gray-300 rounded-full overflow-hidden h-full mr-4 relative">
          <div 
            className="bg-green-500 absolute bottom-0 left-0 right-0 transition-all duration-500 ease-out" 
            style={{ height: `${progressPercent}%` }}
          ></div>
        </div>

        {/* Milestones List */} 
        <div className="flex flex-col justify-between h-full flex-grow">
          {milestones.map((milestone) => {
            const isUnlocked = currentLevel >= milestone.level;
            const featureEnabled = milestone.featureKey ? autoFeatures[milestone.featureKey] : false;
            
            let statusText = 'Locked';
            let statusColor = 'text-gray-500';
            if (isUnlocked) {
              if (milestone.featureKey) { // Only show enabled/disabled if it has a toggle
                statusText = featureEnabled ? 'Enabled' : 'Disabled';
                statusColor = featureEnabled ? 'text-green-600 font-medium' : 'text-red-600 font-medium';
              } else {
                statusText = 'Unlocked'; // For features like Rocket Launcher with no toggle yet
                statusColor = 'text-blue-600 font-medium';
              }
            }

            return (
              <div key={milestone.level} className="mb-2 flex justify-between items-center">
                <div>
                  <span className="font-bold">Lv {milestone.level}: {milestone.name}</span>
                  <span className={`ml-2 text-xs ${statusColor}`}>({statusText})</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FeaturePreview; 